// mark.math.mjs — pricing a loan off its projected cash flows.
// ─────────────────────────────────────────────────────────────────────────────
// Pure arithmetic. No React, no API, no dates from the clock: everything
// arrives as an argument, so the Mark tab and the tests run the same code.
//
// THE MARK
//
//   price (clean, per 100)  = (PV of projected net cash − accrued) / par × 100
//
//   par        the net (balance-sheet) principal outstanding at the as-of
//              date: our share, after participations — the same balance the
//              Portfolio Balance page reports.
//   cash       every projected flow the performance run froze for the loan's
//              current leg: scheduled principal, interest, fees and other,
//              past-due amounts (dated the as-of date), forecast charge-offs,
//              and projected participation payments, which are negative.
//   discount   the forward SOFR curve the run was built on, plus a spread in
//              basis points: simple Actual/360 at the curve rate in force at
//              the start of each period, compounded at every month-end (see
//              discountFactors). Same arithmetic as the Mark Proof workbook.
//   accrued    interest earned since the last scheduled date and not yet paid.
//              It is inside the next payment, so it is inside PV; the clean
//              price takes it back out, which is the market convention.
//
// Why a spread over the curve and not a flat yield: the book is floating. A
// flat discount rate would price a SOFR + 600 loan the same whether SOFR is
// expected to fall or rise, while its coupons would not be the same.
//
// YIELDS (annual effective, Act/365, the same convention as the IRRs)
//
//   asset yield   what the loan returns bought AT PAR (plus accrued): the
//                 flat rate that discounts its cash back to par + accrued.
//   mark yield    what it returns bought AT THE MARK: the flat rate that
//                 discounts its cash back to the dirty PV.
//
//   Asset yield above mark yield is precisely why a loan prices over 100.
// ─────────────────────────────────────────────────────────────────────────────

const DAY = 86400000;
const t = s => Date.parse(String(s).slice(0, 10) + "T00:00:00Z");
const iso = ms => new Date(ms).toISOString().slice(0, 10);

// Forward rate in force on a date: the latest curve point on or before it.
// Before the curve starts, its first point; past its end, its last — the
// same hold-flat rule the projection uses past the end of the curve.
export function makeCurve(points) {
  const pts = (points || [])
    .map(p => ({ ms: t(p.ratedate), rate: Number(p.rate) }))
    .filter(p => isFinite(p.ms) && isFinite(p.rate))
    .sort((a, b) => a.ms - b.ms);
  return ms => {
    if (!pts.length) return 0;
    if (ms < pts[0].ms) return pts[0].rate;
    let lo = 0, hi = pts.length - 1;
    while (lo < hi) {                       // last point with ms <= target
      const mid = (lo + hi + 1) >> 1;
      if (pts[mid].ms <= ms) lo = mid; else hi = mid - 1;
    }
    return pts[lo].rate;
  };
}

// Month-end after a date (the same day if it already is one), as ms.
const eom = ms => {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0);
};

// Discount factor from the as-of date to each cash-flow date, for one spread.
//
// ONE GRID, SHARED WITH THE EXCEL PROOF. The compounding points are the as-of
// date and every month-end after it. Between two grid points the discount is
// simple Actual/360 at the curve rate in force on the first of them plus the
// spread; at each grid point it compounds. A cash flow dated between grid
// points takes the factor at the grid point before it and a simple-interest
// stub to its own date.
//
// Why a grid and not each loan's own payment dates: the Mark Proof workbook
// has to compute the same number with plain formulas, and a per-loan chain is
// not something a spreadsheet can do legibly. On a month-end payer the two
// are identical — its payment dates ARE the grid — which is why a loan whose
// coupon equals the discount rate still prices at exactly 100. A first
// attempt compounding at month STARTS priced that loan at 99.9993.
export function discountFactors({ asof, dates, curve, spreadBps }) {
  const a = t(asof);
  const s = (Number(spreadBps) || 0) / 10000;
  const want = [...new Set(dates.map(d => t(d)))].sort((x, y) => x - y);
  const out = new Map();
  let g = a, dfg = 1;                       // last grid point and its factor
  let next = eom(a) > a ? eom(a) : eom(a + DAY);
  for (const target of want) {
    if (target <= a) { out.set(iso(target), 1); continue; }
    while (next <= target) {                // roll the grid up to the date
      dfg /= 1 + (curve(g) + s) * ((next - g) / DAY) / 360;
      g = next;
      next = eom(g + DAY);
    }
    out.set(iso(target), dfg / (1 + (curve(g) + s) * ((target - g) / DAY) / 360));
  }
  return out;
}

// Flat annual-effective yield that prices `flows` to `price` at `asof`
// (Act/365, like XIRR). Bisection: the PV is monotone in the rate for a
// stream of non-negative cash, which a performing loan's projection is.
export function flatYield({ asof, flows, price }) {
  const a = t(asof);
  const cf = flows.map(f => ({ yrs: Math.max(0, (t(f.dt) - a) / DAY / 365), amt: f.amt }));
  const pv = y => cf.reduce((s, f) => s + f.amt / Math.pow(1 + y, f.yrs), 0);
  if (!(price > 0) || !cf.some(f => f.amt > 0)) return null;
  let lo = -0.9, hi = 5;
  if (pv(lo) < price || pv(hi) > price) return null;    // no rate brackets it
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    if (pv(mid) > price) lo = mid; else hi = mid;
    if (hi - lo < 1e-10) break;
  }
  return (lo + hi) / 2;
}

// Weighted-average life in years: principal-weighted time to repayment.
export function wal({ asof, flows }) {
  const a = t(asof);
  let w = 0, p = 0;
  for (const f of flows) {
    if (!(f.prin > 0)) continue;
    w += f.prin * Math.max(0, (t(f.dt) - a) / DAY / 365);
    p += f.prin;
  }
  return p > 0 ? w / p : null;
}

// Price one loan.
//
//   loan  { par, accrued, flows: [{ dt, amt, prin }] }   amt = net cash that date
//   curve from makeCurve, spreadBps the discount spread
//
// Returns the figures the Mark tab shows; null price when there is no par.
export function priceLoan({ asof, loan, curve, spreadBps, yields = true }) {
  const flows = (loan.flows || []).filter(f => Math.abs(f.amt) > 0.005);
  const par = Number(loan.par) || 0;
  const accrued = Number(loan.accrued) || 0;
  const dfs = discountFactors({ asof, dates: flows.map(f => f.dt), curve, spreadBps });
  const pv = flows.reduce((s, f) => s + f.amt * dfs.get(String(f.dt).slice(0, 10)), 0);
  const cash = flows.map(f => ({ dt: f.dt, amt: f.amt }));
  if (!(par > 0)) {
    return { par, pv, accrued, price: null, mv: pv - accrued, premium: null,
             assetYield: null, markYield: null, wal: null };
  }
  const clean = pv - accrued;
  return {
    par, pv, accrued,
    price: clean / par * 100,
    mv: clean,
    premium: clean - par,
    // Yields are a bisection each; the sensitivity strip prices the book
    // seven times per keystroke and only needs the prices.
    assetYield: yields ? flatYield({ asof, flows: cash, price: par + accrued }) : null,
    markYield: yields ? flatYield({ asof, flows: cash, price: pv }) : null,
    wal: wal({ asof, flows }),
  };
}

// ── WHAT A LOAN IS DISCOUNTED AT ────────────────────────────────────────────
//
//   spread = rating spread + size adjustment + collateral adjustment
//
// unless the loan has its own override, which is the whole spread and beats
// all three. Kept as a sum of small tables rather than one rating × size ×
// collateral matrix: three tables of a handful of cells each can be reasoned
// about; 120 cells of a matrix are mostly guesses.
//
// Size is the deal's COMMITMENT (the full facility, not our hold — the market
// prices the deal). Collateral profile is decided by the server from the
// posted borrowing base and arrives on the loan (see getPerformanceExport).

export const SIZE_BUCKETS = ["<5mm", "5-20mm", ">20mm", "Unknown"];
export const COLLATERAL_PROFILES = ["AR-backed", "Mixed", "Inventory-heavy", "Cash flow", "No collateral data"];

// <5mm below 5,000,000; 5-20mm from 5,000,000 to 20,000,000 inclusive;
// >20mm above. No commitment on file = Unknown.
export function sizeBucket(commitment) {
  const c = Number(commitment);
  if (!(c > 0)) return "Unknown";
  if (c < 5000000) return "<5mm";
  if (c <= 20000000) return "5-20mm";
  return ">20mm";
}

const bpsOf = v => (v !== undefined && v !== null && v !== "" && isFinite(Number(v)) ? Number(v) : null);

// Returns the spread and how it was built, for the table's tooltip.
//   adjustments  { size: { "<5mm": 100, ... }, collateral: { "AR-backed": -50, ... } }
export function spreadFor(loan, grid, overrides, adjustments) {
  const o = overrides && bpsOf(overrides[loan.loanid]);
  if (o !== null && o !== undefined)
    return { bps: o, from: "override", parts: { override: o } };
  const r = loan.rating == null ? "" : String(loan.rating).trim();
  const rated = r && grid && bpsOf(grid[r]) !== null;
  const base = rated ? bpsOf(grid[r]) : (bpsOf(grid && grid.Unrated) ?? 0);
  const size = loan.sizeBucket || sizeBucket(loan.commitment);
  const coll = loan.collateralProfile || "No collateral data";
  const sAdj = bpsOf(adjustments && adjustments.size && adjustments.size[size]) ?? 0;
  const cAdj = bpsOf(adjustments && adjustments.collateral && adjustments.collateral[coll]) ?? 0;
  return {
    bps: base + sAdj + cAdj,
    from: rated ? "rating " + r : "unrated",
    parts: { base, baseFrom: rated ? "rating " + r : "unrated", size, sizeAdj: sAdj,
             collateral: coll, collateralAdj: cAdj },
  };
}

// ── RENEWAL OVERLAY ────────────────────────────────────────────────────────
//
// Loans are written for one to three years for optionality, and performing
// ones are usually renewed for a fee. The SCHEDULE stays contractual — it
// drives invoices and delinquency, and a renewal has not happened until it
// happens. This overlay is the Mark's view of the loan IF it renews:
//
//   at maturity     the balloon does not come back; the renewal fee does —
//                   feePct × the loan's commitment, our share
//   for N months    the balance is carried flat: interest at forward SOFR
//                   (the curve rate at the start of each month) + the loan's
//                   current margin (a fixed loan: its coupon), Act/360, plus
//                   the loan's recurring non-interest income held at its
//                   run-rate (the average of its last three projected months:
//                   monthly fee, unused fee, other — net of participations)
//   at the end      the balloon, N months later, at month-end
//
// Held flat, not amortized: renewals here are mostly revolvers, and a term
// loan's re-amortization is a term of the new deal nobody has written yet.
// The renewed margin is today's margin; a step-down would be a new input.
//
// Returns the renewed flows, or null when there is nothing to renew (no
// principal still to come, or months is 0).
const eomAdd = (dt, k) => {
  const d = new Date(t(dt));
  return iso(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1 + k, 0));
};
export function renewFlows({ flows, curve, margin, fixed, months, feePct, feeBase }) {
  const m = Math.round(Number(months) || 0);
  const fl = (flows || []).slice().sort((a, b) => t(a.dt) - t(b.dt));
  let mi = -1;
  fl.forEach((f, i) => { if ((f.prin || 0) > 0.005) mi = i; });
  if (mi < 0 || !(m > 0)) return null;
  const mat = fl[mi];
  const B = mat.prin;                                   // what would have come back
  const prior = fl.slice(0, mi).slice(-3);
  const runRate = prior.length
    ? prior.reduce((s, f) => s + (f.amt - (f.prin || 0) - (f.intr || 0)), 0) / prior.length : 0;
  const fee = Math.max(0, Number(feePct) || 0) * Math.max(0, Number(feeBase) || 0);
  const out = fl.slice(0, mi).map(f => ({ ...f }));
  out.push({ ...mat, amt: mat.amt - B + fee, prin: 0, fee });
  fl.slice(mi + 1).forEach(f => out.push({ ...f }));   // anything dated after (rare)
  let prev = mat.dt;
  for (let k = 1; k <= m; k++) {
    const dt = eomAdd(mat.dt, k);
    const days = (t(dt) - t(prev)) / DAY;
    const rate = (fixed ? 0 : curve(t(prev))) + (Number(margin) || 0);
    const intr = B * rate * days / 360;
    const last = k === m;
    out.push({ dt, amt: intr + runRate + (last ? B : 0), prin: last ? B : 0, intr, extension: true });
    prev = dt;
  }
  return out;
}

// The renewal settings a loan takes: its own, else the defaults.
export function renewalFor(loan, defaults) {
  const pick = (v, d) => (v !== undefined && v !== null && v !== "" && isFinite(Number(v)) ? Number(v) : d);
  return { months: pick(loan.renewMonths, defaults ? Number(defaults.months) || 0 : 0),
           feePct: pick(loan.renewFeePct, defaults ? Number(defaults.feePct) || 0 : 0) };
}

// The whole book: each loan priced, plus the totals. `shift` moves every
// spread by the same number of bps, for the sensitivity strip. `excluded`
// ({ loanid: true }) keeps a loan in the rows — still priced, flagged — but
// out of every book figure.
export function priceBook({ asof, loans, curvePoints, grid, overrides, shift = 0, yields = true,
                            excluded = {}, adjustments = null, renewal = null }) {
  const curve = makeCurve(curvePoints);
  const rows = loans.map(l => {
    const sp = spreadFor(l, grid, overrides, adjustments);
    // With `renewal` (the defaults), each loan is priced on its renewed flows:
    // its own months / fee if set, else the defaults. Fee base is the loan's
    // commitment at our share — net over gross scheduled principal.
    let fl = l.flows;
    if (renewal) {
      const rs = renewalFor(l, renewal);
      const gross = (l.flows || []).reduce((s, f) => s + (f.sprin || 0), 0);
      const net = (l.flows || []).reduce((s, f) => s + (f.prin || 0), 0);
      const share = gross > 0 ? Math.min(1, Math.max(0, net / gross)) : 1;
      fl = renewFlows({ flows: l.flows, curve, margin: l.margin, fixed: l.fixed,
                        months: rs.months, feePct: rs.feePct,
                        feeBase: (l.loanCommitment || 0) * share }) || l.flows;
    }
    const p = priceLoan({ asof, loan: { ...l, flows: fl }, curve, spreadBps: sp.bps + shift, yields });
    return { ...l, sizeBucket: l.sizeBucket || sizeBucket(l.commitment),
             collateralProfile: l.collateralProfile || "No collateral data",
             spreadBps: sp.bps + shift, spreadFrom: sp.from, spreadParts: sp.parts,
             excluded: !!(excluded && excluded[l.loanid]), ...p };
  });
  const priced = rows.filter(r => r.price !== null && !r.excluded);
  const par = priced.reduce((s, r) => s + r.par, 0);
  const mv  = priced.reduce((s, r) => s + r.mv, 0);
  const acc = priced.reduce((s, r) => s + r.accrued, 0);
  const pv  = priced.reduce((s, r) => s + r.pv, 0);

  // Pooled yields, solved on the combined cash, like the pooled IRRs: a
  // par-weighted average of loan yields would overweight short loans.
  const byDate = new Map();
  priced.forEach(r => (r.flows || []).forEach(f => {
    const k = String(f.dt).slice(0, 10);
    byDate.set(k, (byDate.get(k) || 0) + f.amt);
  }));
  const pooled = [...byDate].map(([dt, amt]) => ({ dt, amt }));
  return {
    rows,
    book: {
      par, mv, accrued: acc, pv,
      price: par > 0 ? mv / par * 100 : null,
      premium: mv - par,
      assetYield: yields && par > 0 ? flatYield({ asof, flows: pooled, price: par + acc }) : null,
      markYield: yields && par > 0 ? flatYield({ asof, flows: pooled, price: pv }) : null,
      wal: wal({ asof, flows: priced.flatMap(r => r.flows || []) }),
      loans: priced.length,
      excluded: rows.filter(r => r.excluded).length,
    },
  };
}
